// Seller "My Listings" API module (USE_REAL_API-gated via apiUrl + MSW in maket).
//
// Listing-grain data for the Listadum-grade grid: one row per store listing,
// variations nested. Mirrors backend app/api/v1/seller_listings.py. The Listadum
// performance columns (views / scores / sales / revenue) are null until a future
// Etsy listing-analytics sync lands — the grid renders them as "N/A".

import { apiUrl } from '../lib/api'

export type SellerListingVariation = {
  id: string
  // null ONLY for missing-SKU variants (channel variant with no SKU set).
  store_sku: string | null
  sku_id: string | null
  // null when the variation's channel SKU is not yet bound to a PEXIFY product.
  master_product_id: string | null
  product_name: string | null
  // Channel-side variant identity/label (provider-neutral mirror rows only).
  external_variant_id?: string | null
  variant_title?: string | null
  stock: number
  status: string // active | paused | unbound | missing_sku
}

export type SellerListingRow = {
  id: string
  store_listing_id: string | null
  title: string
  channel: string
  store_name: string
  store_integration_id: string
  variation_count: number
  total_stock: number
  status: string
  // mapped | partial | unmapped | conflict (from the listing mirrors).
  mapping_status: string
  // Variants whose channel SKU field is empty — composes with mapping_status
  // (e.g. "Partial · 2 missing SKU"). Always 0 on Etsy rows in Phase A.
  missing_sku_count?: number
  // Not-yet-synced performance columns → null → rendered "N/A".
  completion_score: number | null
  engagement_score: number | null
  views: number | null
  daily_views: number | null
  sales: number | null
  revenue_usd: number | null
  conversion_rate: number | null
  favorites: number | null
  favorite_ratio: number | null
  age_days: number | null
  last_edited: string | null
  variations: SellerListingVariation[]
}

export type SellerListingsResponse = {
  data: SellerListingRow[]
  meta: {
    page: number
    per_page: number
    total: number
    total_pages: number
    facets: {
      all: number
      live: number
      not_live: number
      archived: number
      issues: number
    }
  }
}

export type SellerStoreRow = {
  id: string
  provider: string
  shop_name: string
  has_access_token?: boolean
  active?: boolean
  sync_status?: string
  last_sync_at?: string | null
}

export type MyListingsParams = {
  store_integration_id?: string
  status?: string
  status_group?: 'all' | 'live' | 'not_live' | 'archived'
  issues?: boolean
  q?: string
  sort?: 'title' | 'variations' | 'stock' | 'last_edited'
  sort_dir?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

export async function fetchMyListings(
  params: MyListingsParams = {},
): Promise<SellerListingsResponse> {
  const qs = new URLSearchParams()
  if (params.store_integration_id) qs.set('store_integration_id', params.store_integration_id)
  if (params.status) qs.set('status', params.status)
  if (params.status_group) qs.set('status_group', params.status_group)
  if (params.issues) qs.set('issues', 'true')
  if (params.q && params.q.trim() !== '') qs.set('q', params.q.trim())
  if (params.sort) qs.set('sort', params.sort)
  if (params.sort_dir) qs.set('sort_dir', params.sort_dir)
  if (params.page) qs.set('page', String(params.page))
  if (params.per_page) qs.set('per_page', String(params.per_page))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const res = await fetch(apiUrl(`/seller/my-listings${suffix}`))
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<SellerListingsResponse>
}

export async function fetchSellerStores(): Promise<{ items: SellerStoreRow[] }> {
  const res = await fetch(apiUrl('/store-integrations'))
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<{ items: SellerStoreRow[] }>
}

export type SyncListingsResult = {
  ok: boolean
  cooldown_remaining: number
  summary: Record<string, number> | null
}

// Manual "Sync now" — re-pull one store's live channel listings (Etsy or
// Shopify; rate-limited — ok=false + cooldown_remaining within the per-org
// cooldown window, which is shared across providers).
export async function syncStoreListings(storeId: string): Promise<SyncListingsResult> {
  const res = await fetch(apiUrl(`/seller/stores/${storeId}/sync-listings`), {
    method: 'POST',
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<SyncListingsResult>
}

// Buyer-facing Etsy listing URL for the read-only "View on Etsy" deep link.
export function etsyListingUrl(row: SellerListingRow): string | null {
  if (row.channel !== 'etsy' || !row.store_listing_id) return null
  return `https://www.etsy.com/listing/${row.store_listing_id}`
}

// Seller-facing Etsy listing editor (price, stock, photos). PEXIFY has no page
// of its own for a published store listing, so the listing title opens Etsy's
// editor. Etsy resolves `me` to the shop the seller is signed in to.
export function etsyListingEditUrl(row: SellerListingRow): string | null {
  if (row.channel !== 'etsy' || !row.store_listing_id) return null
  return `https://www.etsy.com/your/shops/me/listing-editor/edit/${encodeURIComponent(row.store_listing_id)}`
}
